// src/contexts/AppContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { organizationsAPI, notificationsAPI } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

const getUserRole = () => {
  const token = localStorage.getItem('idToken');
  if (!token) return 'super_admin'; // Default fallback

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const groups = payload['cognito:groups'] || [];

    if (groups.includes('SuperAdmins')) return 'super_admin';
    if (groups.includes('OrganizationManager')) return 'organization_manager';
    if (groups.includes('Trainers')) return 'trainer';
    if (groups.includes('Students')) return 'student';

    // If no core role, return the first group name (custom role)
    if (groups.length > 0) return groups[0];

    return 'student'; // Safest fallback
  } catch (error) {
    console.error('Error parsing token:', error);
    return 'student'; // Safest fallback
  }
};

export const AppProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    return localStorage.getItem('selectedOrganizationName') || null;
  });
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(() => {
    return localStorage.getItem('selectedOrganizationId') || null;
  });
  const [currentPage, setCurrentPage] = useState('Sessions');
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('super_admin');
  const [userId, setUserId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const hasInitialized = useRef(false);

  // Initialize user role and ID
  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);

    const token = localStorage.getItem('idToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.sub);
      } catch (e) {
        console.error('Failed to parse token for userId', e);
      }
    }
  }, []);

  // Load organizations on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      console.log('AppProvider mounted, fetching organizations...');
      hasInitialized.current = true;
      fetchOrganizations();
    }
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getAll();
      let orgsData = response.data.data || [];
      
      const role = getUserRole();
      
      // If super admin, add "All Organizations" option
      if (role === 'super_admin' && orgsData.length > 0) {
        const allOrgsOption = { id: 'all', name: 'All Organizations' };
        if (!orgsData.some(o => o.id === 'all')) {
          orgsData = [allOrgsOption, ...orgsData];
        }
      }
      
      setOrganizations(orgsData);

      const savedOrgId = localStorage.getItem('selectedOrganizationId');
      
      // Handle "all" explicitly
      if (savedOrgId === 'all' && role === 'super_admin') {
        setSelectedOrganization('All Organizations');
        setSelectedOrganizationId('all');
      } else if (savedOrgId && orgsData.some(org => org.id === savedOrgId)) {
        // If saved org ID exists and is valid, use it
        const savedOrg = orgsData.find(org => org.id === savedOrgId);
        setSelectedOrganization(savedOrg.name);
        setSelectedOrganizationId(savedOrg.id);
      } else if (orgsData.length > 0 && !selectedOrganizationId) {
        // Otherwise set first organization as default if none selected yet
        setSelectedOrganization(orgsData[0].name);
        setSelectedOrganizationId(orgsData[0].id);
        // Save to localStorage
        localStorage.setItem('selectedOrganizationId', orgsData[0].id);
        localStorage.setItem('selectedOrganizationName', orgsData[0].name);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      // Set fallback organizations if API fails
      const fallbackOrgs = [
        { id: '681321b8-bf6a-4299-9b1d-2824cb57a222', name: 'The Athletics Sports Club' }
      ];
      
      if (!selectedOrganizationId) {
        setOrganizations(fallbackOrgs);
        setSelectedOrganization(fallbackOrgs[0].name);
        setSelectedOrganizationId(fallbackOrgs[0].id);
        localStorage.setItem('selectedOrganizationId', fallbackOrgs[0].id);
        localStorage.setItem('selectedOrganizationName', fallbackOrgs[0].name);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    if (!selectedOrganization || organizations.length === 0) return;

    // Resolve effective organization ID
    let orgId = 'all';

    // Check if "All Organizations" is selected
    const isAllOrgs = selectedOrganization === 'Tüm Organizasyonlar' ||
      selectedOrganization === 'All Organizations';

    if (!isAllOrgs) {
      const org = organizations.find(o => o.name === selectedOrganization);
      if (org) orgId = org.id;
    }

    // Don't fetch if no change in request params? (Simple debounce or similar could go here but let's keep simple)
    // Avoid double fetching if already loading? Maybe not, user might want to refresh.

    try {
      setNotificationsLoading(true);
      const response = await notificationsAPI.getAll(orgId);
      const notifs = response.data.data || [];
      // Sort by date desc
      const sorted = notifs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setNotifications(sorted);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Launch Websocket
  useWebSocket();

  // Load initial notifications and set up WebSocket listener
  useEffect(() => {
    if (selectedOrganization && organizations.length > 0) {
      fetchNotifications();
    }
  }, [selectedOrganization, organizations.length]);

  useEffect(() => {
    const handleWsMessage = (event) => {
      const message = event.detail;
      if (message.action === 'NOTIFICATION_RECEIVED' && message.data) {
        setNotifications(prev => {
          // Check if already in list to avoid duplicates
          if (prev.some(n => n.id === message.data.id)) return prev;
          
          return [message.data, ...prev];
        });
      }
    };

    window.addEventListener('ws-message', handleWsMessage);
    return () => window.removeEventListener('ws-message', handleWsMessage);
  }, []);

  const handleOrganizationChange = (orgName) => {
    console.log('Changing organization to:', orgName);
    setSelectedOrganization(orgName);
    localStorage.setItem('selectedOrganizationName', orgName);
    
    // Handle "All" option
    if (orgName === 'All Organizations' || orgName === 'Tüm Organizasyonlar') {
      setSelectedOrganizationId('all');
      localStorage.setItem('selectedOrganizationId', 'all');
      return;
    }

    const org = organizations.find(o => o.name === orgName);
    if (org) {
      console.log('Found organization ID:', org.id);
      setSelectedOrganizationId(org.id);
      localStorage.setItem('selectedOrganizationId', org.id);
    } else {
      console.warn('Organization not found in list:', orgName);
    }
  };

  const addOrganization = (newOrg) => {
    setOrganizations(prev => [...prev, newOrg]);
  };

  const updateOrganization = (updatedOrg) => {
    setOrganizations(prev =>
      prev.map(org => org.id === updatedOrg.id ? updatedOrg : org)
    );
    // Update selected organization name if it was the one updated
    if (selectedOrganizationId === updatedOrg.id) {
      setSelectedOrganization(updatedOrg.name);
    }
  };

  const removeOrganization = (orgId) => {
    setOrganizations(prev => prev.filter(org => org.id !== orgId));
    // If deleted organization was selected, select first remaining one
    if (selectedOrganizationId === orgId) {
      const remaining = organizations.filter(org => org.id !== orgId);
      if (remaining.length > 0) {
        setSelectedOrganization(remaining[0].name);
        setSelectedOrganizationId(remaining[0].id);
        // Save to localStorage
        localStorage.setItem('selectedOrganizationId', remaining[0].id);
      } else {
        setSelectedOrganization(null);
        setSelectedOrganizationId(null);
        // Remove from localStorage
        localStorage.removeItem('selectedOrganizationId');
      }
    }
  };

  // Helper functions for role checking
  const isSuperAdmin = () => {
    return userRole === 'super_admin';
  };

  const isTrainer = () => {
    return userRole === 'trainer';
  };

  const isOrganizationManager = () => {
    return userRole === 'organization_manager';
  };

  const canApproveExercises = () => {
    return userRole === 'super_admin' || userRole === 'organization_manager';
  };

  const canManageOrganizations = () => {
    return userRole === 'super_admin';
  };

  const canAssignTrainers = () => {
    return userRole === 'super_admin';
  };

  const value = {
    // Your existing properties
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    setSelectedOrganization: handleOrganizationChange,
    currentPage,
    setCurrentPage,
    loading,
    fetchOrganizations,
    addOrganization,
    updateOrganization,
    removeOrganization,

    // Notifications
    notifications,
    notificationsLoading,
    fetchNotifications,

    // Role properties
    userRole,
    userId,
    accessLevel: userRole === 'super_admin' ? 'all' : 'filtered',
    isSuperAdmin,
    isTrainer,
    isOrganizationManager,
    canApproveExercises,
    canManageOrganizations,
    canAssignTrainers
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};